import React from "react";
import styled from "styled-components";
import { PersonalInfo } from "../types";
import { Button } from "../styles/GlobalStyles";
import { useLanguage } from "../contexts/LanguageContext";

const HeroSection = styled.section`
  min-height: 100vh;
  display: flex;
  align-items: center;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  position: relative;
  overflow: hidden;

  @media (max-width: 768px) {
    padding-top: 80px;
    min-height: calc(100vh - 80px);
  }

  @media (max-width: 480px) {
    padding-top: 70px;
    min-height: calc(100vh - 70px);
  }

  &::before {
    content: "";
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: url('data:image/svg+xml,<svg width="60" height="60" viewBox="0 0 60 60" xmlns="http://www.w3.org/2000/svg"><g fill="none" fill-rule="evenodd"><g fill="%23ffffff" fill-opacity="0.1"><circle cx="30" cy="30" r="2"/></g></svg>')
      repeat;
    animation: float 20s ease-in-out infinite;
  }

  @keyframes float {
    0%,
    100% {
      transform: translateY(0);
    }
    50% {
      transform: translateY(-20px);
    }
  }
`;

const HeroContent = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 2rem;
  text-align: center;
  color: white;
  z-index: 1;
  position: relative;

  @media (max-width: 480px) {
    padding: 0 1rem;
  }
`;

const ProfileImage = styled.div`
  width: 200px;
  height: 200px;
  border-radius: 50%;
  background: white;
  margin: 0 auto 2rem;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 4rem;
  color: #667eea;
  box-shadow: 0 20px 40px rgba(0, 0, 0, 0.2);
  position: relative;

  @media (max-width: 480px) {
    width: 150px;
    height: 150px;
    font-size: 3rem;
    margin: 0 auto 1.5rem;
  }

  &::after {
    content: "";
    position: absolute;
    top: -10px;
    left: -10px;
    right: -10px;
    bottom: -10px;
    border: 3px solid rgba(255, 255, 255, 0.3);
    border-radius: 50%;
    animation: pulse 3s ease-in-out infinite;
  }

  @keyframes pulse {
    0%,
    100% {
      transform: scale(1);
      opacity: 0.7;
    }
    50% {
      transform: scale(1.05);
      opacity: 1;
    }
  }

  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
    border-radius: 50%;
  }
`;

const Title = styled.h1`
  font-size: 3.5rem;
  margin-bottom: 1rem;
  font-weight: 700;

  @media (max-width: 768px) {
    font-size: 2.5rem;
  }

  @media (max-width: 480px) {
    font-size: 2rem;
    margin-bottom: 0.8rem;
  }
`;

const Subtitle = styled.h2`
  font-size: 1.5rem;
  margin-bottom: 1.5rem;
  font-weight: 300;
  opacity: 0.9;

  @media (max-width: 768px) {
    font-size: 1.2rem;
  }

  @media (max-width: 480px) {
    font-size: 1.1rem;
    margin-bottom: 1rem;
  }
`;

const Bio = styled.p`
  font-size: 1.2rem;
  margin-bottom: 3rem;
  max-width: 600px;
  margin-left: auto;
  margin-right: auto;
  opacity: 0.9;
  line-height: 1.8;

  @media (max-width: 768px) {
    font-size: 1rem;
  }
`;

const ButtonGroup = styled.div`
  display: flex;
  gap: 1rem;
  justify-content: center;
  flex-wrap: wrap;
`;

const HeroButton = styled(Button)`
  background: ${(props) =>
    props.variant === "primary" ? "white" : "transparent"};
  color: ${(props) => (props.variant === "primary" ? "#667eea" : "white")};
  border: ${(props) =>
    props.variant === "primary" ? "none" : "2px solid white"};

  &:hover {
    background: ${(props) =>
      props.variant === "primary" ? "#f8f9fa" : "white"};
    color: #667eea;
    transform: translateY(-2px);
  }
`;

interface HeroProps {
  personalInfo: PersonalInfo;
}

const Hero: React.FC<HeroProps> = ({ personalInfo }) => {
  const { t, language } = useLanguage();

  const scrollToSection = (sectionId: string) => {
    const section = document.getElementById(sectionId);
    if (section) {
      const offsetTop = section.offsetTop - 80;
      window.scrollTo({
        top: offsetTop,
        behavior: "smooth",
      });
    }
  };

  return (
    <HeroSection id="home">
      <HeroContent>
        <ProfileImage>
          {personalInfo.profileImage ? (
            <img src={personalInfo.profileImage} alt={personalInfo.name} />
          ) : (
            personalInfo.name.charAt(0).toUpperCase()
          )}
        </ProfileImage>

        <Title>{personalInfo.name}</Title>
        <Subtitle>{t("hero.title")}</Subtitle>
        <Bio>{t("hero.bio")}</Bio>

        <ButtonGroup>
          <HeroButton
            variant="primary"
            onClick={() => scrollToSection("projects")}
          >
            {t("hero.viewProjects")}
          </HeroButton>
          <HeroButton
            variant="secondary"
            onClick={() => scrollToSection("contact")}
          >
            {t("hero.contact")}
          </HeroButton>
          <HeroButton
            as="a"
            href={`${process.env.PUBLIC_URL}/assets/JOBIT_RESUME.pdf`}
            download="JOBIT_RESUME.pdf"
            target="_blank"
            rel="noopener noreferrer"
          >
            📄 {t("hero.downloadCv")}
          </HeroButton>
        </ButtonGroup>
      </HeroContent>
    </HeroSection>
  );
};

export default Hero;
